import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Key, Save, TestTube, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useHubSpotConfig, useSaveHubSpotConfig } from '@/hooks/useHubSpot';
import { getHubSpotClient } from '@/services/hubspot/hubspotClient';
import { toast } from 'sonner';

export default function HubSpotSetup() {
  const navigate = useNavigate();
  const { data: existingConfig } = useHubSpotConfig();
  const { mutate: saveConfig, isPending: saving } = useSaveHubSpotConfig();

  const [config, setConfig] = useState({
    api_key_encrypted: existingConfig?.api_key_encrypted || '',
    portal_id: existingConfig?.portal_id || '',
    sync_enabled: existingConfig?.sync_enabled || false,
    sync_schedule: existingConfig?.sync_schedule || { frequency: 'manual' },
  });

  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleTestConnection = async () => {
    if (!config.api_key_encrypted) {
      toast.error('Please enter an API key');
      return;
    }

    setTestingConnection(true);
    setConnectionStatus('idle');

    try {
      // Create a temporary client with the test API key
      const testClient = await getHubSpotClient();
      
      // Try to fetch account info
      const response = await fetch('https://api.hubapi.com/account-info/v3/details', {
        headers: {
          'Authorization': `Bearer ${config.api_key_encrypted}`,
        },
      });

      if (response.ok) {
        const accountInfo = await response.json();
        setConfig(prev => ({ ...prev, portal_id: accountInfo.portalId.toString() }));
        setConnectionStatus('success');
        toast.success('Connection successful!');
      } else {
        setConnectionStatus('error');
        toast.error('Invalid API key or insufficient permissions');
      }
    } catch (error) {
      setConnectionStatus('error');
      toast.error('Failed to connect to HubSpot');
    } finally {
      setTestingConnection(false);
    }
  };

  const handleSave = () => {
    if (!config.api_key_encrypted) {
      toast.error('Please enter an API key');
      return;
    }

    saveConfig(config, {
      onSuccess: () => {
        navigate('/integrations/hubspot');
      },
    });
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/integrations/hubspot')}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold">HubSpot Setup</h1>
          <p className="text-muted-foreground mt-1">
            Configure your HubSpot integration settings
          </p>
        </div>
      </div>

      <Tabs defaultValue="authentication" className="space-y-4">
        <TabsList>
          <TabsTrigger value="authentication">Authentication</TabsTrigger>
          <TabsTrigger value="sync-settings">Sync Settings</TabsTrigger>
          <TabsTrigger value="advanced">Advanced</TabsTrigger>
        </TabsList>

        <TabsContent value="authentication" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>API Authentication</CardTitle>
              <CardDescription>
                Connect to HubSpot using your private app access token
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  To get your API key:
                  <ol className="list-decimal list-inside mt-2 space-y-1">
                    <li>Log in to your HubSpot account</li>
                    <li>Go to Settings → Integrations → Private Apps</li>
                    <li>Create a new private app or use an existing one</li>
                    <li>Ensure it has permissions for Contacts, Companies, and Deals</li>
                    <li>Copy the access token</li>
                  </ol>
                </AlertDescription>
              </Alert>

              <div className="space-y-2">
                <Label htmlFor="api-key">API Access Token</Label>
                <Input
                  id="api-key"
                  type="password"
                  placeholder="pat-na1-xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  value={config.api_key_encrypted}
                  onChange={(e) => setConfig({ ...config, api_key_encrypted: e.target.value })}
                />
              </div>

              {config.portal_id && (
                <div className="space-y-2">
                  <Label htmlFor="portal-id">Portal ID</Label>
                  <Input
                    id="portal-id"
                    value={config.portal_id}
                    disabled
                    className="bg-muted"
                  />
                </div>
              )}

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={handleTestConnection}
                  disabled={testingConnection || !config.api_key_encrypted}
                >
                  <TestTube className="h-4 w-4 mr-2" />
                  {testingConnection ? 'Testing...' : 'Test Connection'}
                </Button>

                {connectionStatus === 'success' && (
                  <div className="flex items-center text-green-600">
                    <span className="text-sm">Connected successfully!</span>
                  </div>
                )}
                {connectionStatus === 'error' && (
                  <div className="flex items-center text-red-600">
                    <span className="text-sm">Connection failed</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="sync-settings" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Synchronization Settings</CardTitle>
              <CardDescription>
                Configure how data syncs between HubSpot and your CRM
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Enable Automatic Sync</Label>
                  <p className="text-sm text-muted-foreground">
                    Automatically sync data on a schedule
                  </p>
                </div>
                <Switch
                  checked={config.sync_enabled}
                  onCheckedChange={(checked) => 
                    setConfig({ ...config, sync_enabled: checked })
                  }
                />
              </div>

              {config.sync_enabled && (
                <div className="space-y-2">
                  <Label>Sync Frequency</Label>
                  <Select
                    value={config.sync_schedule.frequency}
                    onValueChange={(value) => 
                      setConfig({
                        ...config,
                        sync_schedule: { ...config.sync_schedule, frequency: value as any }
                      })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manual">Manual Only</SelectItem>
                      <SelectItem value="hourly">Every Hour</SelectItem>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Entity Selection</CardTitle>
              <CardDescription>
                Choose which HubSpot entities to sync
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Contacts</Label>
                  <Switch defaultChecked />
                </div>
                <div className="flex items-center justify-between">
                  <Label>Companies</Label>
                  <Switch defaultChecked />
                </div>
                <div className="flex items-center justify-between">
                  <Label>Deals</Label>
                  <Switch defaultChecked />
                </div>
                <div className="flex items-center justify-between">
                  <Label>Engagements</Label>
                  <Switch defaultChecked />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="advanced" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Advanced Settings</CardTitle>
              <CardDescription>
                Additional configuration options
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Webhook Secret</Label>
                <Input
                  type="password"
                  placeholder="Optional webhook verification secret"
                  value={config.webhook_secret || ''}
                  onChange={(e) => 
                    setConfig({ ...config, webhook_secret: e.target.value })
                  }
                />
                <p className="text-sm text-muted-foreground">
                  Used to verify webhook requests from HubSpot
                </p>
              </div>

              <div className="space-y-2">
                <Label>Rate Limiting</Label>
                <p className="text-sm text-muted-foreground">
                  API calls are automatically rate-limited to respect HubSpot's limits
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Actions */}
      <div className="flex justify-end gap-2">
        <Button
          variant="outline"
          onClick={() => navigate('/integrations/hubspot')}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving || !config.api_key_encrypted}
        >
          <Save className="h-4 w-4 mr-2" />
          Save Configuration
        </Button>
      </div>
    </div>
  );
}